import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminPage } from "./pages/AdminPage";
import { AuthPage } from "./pages/AuthPage";
import { DashboardPage } from "./pages/DashboardPage";
import { PublicPage } from "./pages/PublicPage";
import { ProjectDetailsPage } from "./pages/ProjectDetailsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { PlatformAdminPage } from "./pages/PlatformAdminPage";
import { useAuth } from "./lib/auth";
import { canAccessPlatformAdmin } from "./lib/platform-admin";
import { loadEditorRoute } from "./editor/editor-route-loader";
import {
  ContactPage,
  DownloadsPage,
  HelpPage,
  LegalPage,
  PricingPage,
  SketchUpPage,
  UsagePage,
} from "./pages/SupportPages";

const EditorPage = lazy(() =>
  loadEditorRoute().then((module) => ({ default: module.EditorPage })),
);
const AnnotationPage = lazy(() =>
  import("./pages/AnnotationPage").then((module) => ({ default: module.AnnotationPage })),
);
const LabelDesignerPage = lazy(() =>
  import("./pages/LabelDesignerPage").then((module) => ({ default: module.LabelDesignerPage })),
);

function ProtectedShell() {
  return (
    <ProtectedRoute>
      <AppShell>
        <Outlet />
      </AppShell>
    </ProtectedRoute>
  );
}

function PlatformAdminRoute() {
  const { session } = useAuth();
  return canAccessPlatformAdmin(session?.platformRoles ?? []) ? <PlatformAdminPage /> : <Navigate to="/dashboard" replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<PublicPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/downloads" element={<DownloadsPage />} />
      <Route path="/integracoes/sketchup" element={<SketchUpPage />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/cookies" element={<LegalPage kind="cookies" />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/help/:locale" element={<HelpPage />} />
      <Route path="/help/:locale/:slug" element={<HelpPage />} />

      <Route element={<ProtectedShell />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/settings" element={<SettingsPage key="workspace" scope="workspace" />} />
        <Route path="/account" element={<SettingsPage key="account" scope="account" />} />
        <Route path="/plan-usage" element={<UsagePage />} />
        {/* Produção passou a viver dentro do projeto, no editor. As rotas
            históricas levam à lista de projetos, de onde se escolhe um e se
            entra — que é o caminho que elas pediam de qualquer forma. */}
        <Route path="/assembly" element={<Navigate to="/dashboard" replace />} />
        <Route path="/production" element={<Navigate to="/dashboard" replace />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/platform-admin" element={<PlatformAdminRoute />} />
        <Route path="/internal/skills" element={<Navigate to="/platform-admin" replace />} />
        <Route path="/projects/:projectId" element={<ProjectDetailsPage />} />
        <Route path="/labels/:templateId" element={<Suspense fallback={<main className="editor-loading"><strong>Preparando o designer…</strong></main>}><LabelDesignerPage /></Suspense>} />
      </Route>
      <Route
        path="/editor"
        element={
          <ProtectedRoute>
            <Suspense fallback={<main className="editor-loading"><strong>Preparando o editor técnico…</strong></main>}>
              <EditorPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/annotations/:annotationId"
        element={<ProtectedRoute><Suspense fallback={<main className="editor-loading"><strong>Preparando a prancheta…</strong></main>}><AnnotationPage /></Suspense></ProtectedRoute>}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
